/**
 * Duplicate Detection Module
 * Performs in-memory comparison of contacts to identify duplicates
 *
 * Key Decisions:
 * - Phone matching takes priority over email matching
 * - Only PRIMARY phone/email are indexed (not alternates)
 * - First match wins (no complex merge chains)
 *
 * The API-side sync path applies this same-person policy too, via
 * @super/shared-types `unifyRecordsBySharedContactInfo` — when changing the
 * matching rules here, check whether that site must change too.
 */

import type {
  ContactPropertyRelationshipRecord,
  ContactWithRelationships,
} from '@super/shared-types'

// Existing contact from database
export type ExistingContact = ContactWithRelationships
// New contact from CSV upload
export type UploadContact = ContactPropertyRelationshipRecord
// Either type for grouping operations
export type GroupMemberContact = ExistingContact | UploadContact

export interface PairwiseMatch {
  contact1: GroupMemberContact
  contact2: GroupMemberContact
  matchType: 'relationship' | 'phone' | 'email'
}

export interface UnifiedContactGroup {
  contacts: GroupMemberContact[]
  primaryContact: GroupMemberContact
  pairwiseMatches: PairwiseMatch[]

  // Simple: Is this an update or create operation?
  operation: 'update' | 'create'

  // If updating, what's the target contact ID?
  existingContactId?: string

  // If updating, here's the full existing contact details
  existingContact?: ExistingContact
}

export interface DuplicateDetectionResult {
  // Unified groups containing all related contacts (CSV + database)
  groups: UnifiedContactGroup[]
  // Contacts with no matches at all
  uniqueContacts: UploadContact[]
}

/**
 * Union-Find (Disjoint Set Union) data structure for efficient group management
 * Used to track transitive relationships between contacts
 *
 * Example: If A matches B (by phone) and B matches C (by email),
 * then A, B, and C are all in the same group
 */
class UnionFind {
  private parent: Map<number, number> = new Map()
  private rank: Map<number, number> = new Map()

  constructor(size: number) {
    // Initially, each element is its own parent (separate group)
    for (let i = 0; i < size; i++) {
      this.parent.set(i, i)
      this.rank.set(i, 0)
    }
  }

  /**
   * Find the root parent of an element (which group it belongs to)
   * Uses path compression for optimization
   */
  find(x: number): number {
    if (this.parent.get(x) !== x) {
      // Path compression: make x point directly to root
      this.parent.set(x, this.find(this.parent.get(x)!))
    }
    return this.parent.get(x)!
  }

  /**
   * Unite two groups (mark two elements as belonging to same group)
   * Uses union by rank for optimization
   */
  union(x: number, y: number): void {
    const rootX = this.find(x)
    const rootY = this.find(y)

    if (rootX === rootY) return // Already in same group

    // Union by rank: attach smaller tree under larger tree
    const rankX = this.rank.get(rootX)!
    const rankY = this.rank.get(rootY)!

    if (rankX < rankY) {
      this.parent.set(rootX, rootY)
    } else if (rankX > rankY) {
      this.parent.set(rootY, rootX)
    } else {
      this.parent.set(rootY, rootX)
      this.rank.set(rootX, rankX + 1)
    }
  }

  /**
   * Get all groups as a map of root → members
   */
  getGroups(): Map<number, number[]> {
    const groups = new Map<number, number[]>()

    for (let i = 0; i < this.parent.size; i++) {
      const root = this.find(i)
      if (!groups.has(root)) {
        groups.set(root, [])
      }
      groups.get(root)!.push(i)
    }

    return groups
  }
}

/**
 * Detects duplicates between uploaded contacts and existing contacts
 * Priority order:
 * 1. Relationship external ID (highest priority - idempotency)
 * 2. Phone (primary only)
 * 3. Email (primary only)
 */
export class DuplicateDetector {
  // Source tracking for all contacts
  private contactSourceMap = new Map<
    GroupMemberContact,
    'database' | 'upload'
  >()

  // Level 1: Relationship external ID → Contact mapping
  private relationshipIndex: Map<string, ContactWithRelationships> = new Map()

  // Level 2 & 3: Only index PRIMARY values (not alternates)
  private primaryPhoneIndex: Map<string, ContactWithRelationships> = new Map()
  private primaryEmailIndex: Map<string, ContactWithRelationships> = new Map()

  constructor(private existingContacts: ContactWithRelationships[]) {
    this.buildIndexes(existingContacts)
    // Initialize source map with existing contacts
    existingContacts.forEach(contact => {
      this.contactSourceMap.set(contact, 'database')
    })
  }

  private buildIndexes(contacts: ContactWithRelationships[]) {
    for (const contact of contacts) {
      // Build relationship index (Level 1)
      // Each contact's relationships are indexed by their external IDs
      if (contact.relationships) {
        for (const relationship of contact.relationships) {
          if (relationship.relationshipExternalId) {
            this.relationshipIndex.set(
              relationship.relationshipExternalId,
              contact,
            )
          }
        }
      }

      // Build phone index (Level 2)
      if (contact.primaryPhoneNumber) {
        this.primaryPhoneIndex.set(contact.primaryPhoneNumber, contact)
      }

      // Build email index (Level 3)
      if (contact.primaryEmail) {
        const emailKey = contact.primaryEmail.toLowerCase()
        this.primaryEmailIndex.set(emailKey, contact)
      }
    }
  }

  /**
   * Detect duplicates in uploaded contacts - Two-Phase Approach
   * Phase 1: Build groups within batch using transitive matching (Union-Find)
   * Phase 2: Match groups against existing database contacts
   */
  detectDuplicates(
    uploadedContacts: UploadContact[],
  ): DuplicateDetectionResult {
    // Filter out contacts without any contact method (email or phone)
    // These were warned about in the preview step and should not be processed
    const validUploadedContacts = uploadedContacts.filter(
      contact => contact.primaryEmail || contact.primaryPhoneNumber,
    )

    // Phase 1: Add valid upload contacts to source map
    validUploadedContacts.forEach(contact => {
      this.contactSourceMap.set(contact, 'upload')
    })

    // Phase 2: Create unified contact array (existing + valid uploaded)
    const allContacts: GroupMemberContact[] = [
      ...this.existingContacts,
      ...validUploadedContacts,
    ]

    // Phase 2: Build unified groups using Union-Find
    const unifiedGroups = this.buildUnifiedGroups(allContacts)

    // Phase 3: Categorize groups and individual contacts
    const groups: UnifiedContactGroup[] = []
    const uniqueContacts: UploadContact[] = []

    for (const group of unifiedGroups) {
      // Check if group contains any upload contacts
      const hasUploadContact = group.contacts.some(c => this.isUploadContact(c))

      // Skip groups that only contain existing contacts (no uploads)
      if (!hasUploadContact) {
        continue
      }

      if (
        group.contacts.length === 1 &&
        this.isUploadContact(group.contacts[0])
      ) {
        // Single upload contact with no matches
        uniqueContacts.push(group.contacts[0])
      } else {
        // Multi-contact group or mixed group (has at least one upload contact)
        const hasExisting = group.contacts.some(c => this.isExistingContact(c))
        const existingContact = group.contacts.find(c =>
          this.isExistingContact(c),
        ) as ExistingContact | undefined

        groups.push({
          contacts: group.contacts,
          primaryContact: existingContact || group.contacts[0], // Existing contact takes priority
          pairwiseMatches: group.pairwiseMatches,
          operation: hasExisting ? 'update' : 'create',
          existingContactId: existingContact?.id,
          existingContact: existingContact,
        })
      }
    }

    console.log('Unified duplicate detection results:', {
      totalGroups: groups.length,
      uniqueContacts: uniqueContacts.length,
      updateGroups: groups.filter(g => g.operation === 'update').length,
      createGroups: groups.filter(g => g.operation === 'create').length,
    })

    return { groups, uniqueContacts }
  }

  /**
   * Helper to add an index to a Map<string, number[]>
   */
  private addToIndex(
    index: Map<string, number[]>,
    key: string,
    contactIndex: number,
  ): void {
    if (!index.has(key)) {
      index.set(key, [])
    }
    index.get(key)!.push(contactIndex)
  }

  /**
   * Type guard to check if contact is from database using source map
   */
  private isExistingContact(
    contact: GroupMemberContact,
  ): contact is ExistingContact {
    return this.contactSourceMap.get(contact) === 'database'
  }

  /**
   * Type guard to check if contact is from CSV upload using source map
   */
  private isUploadContact(
    contact: GroupMemberContact,
  ): contact is UploadContact {
    return this.contactSourceMap.get(contact) === 'upload'
  }

  /**
   * Build unified groups from all contacts (existing + uploaded)
   */
  private buildUnifiedGroups(allContacts: GroupMemberContact[]): Array<{
    contacts: GroupMemberContact[]
    pairwiseMatches: PairwiseMatch[]
  }> {
    const unionFind = new UnionFind(allContacts.length)
    const pairwiseMatches: PairwiseMatch[] = []

    // Step 1: Index all contacts by their matching fields
    const matchingIndexes = this.buildUnifiedMatchingIndexes(allContacts)

    // Step 2: Group contacts with shared values and track pairwise matches
    this.unionContactsBySharedValuesUnified(
      unionFind,
      matchingIndexes,
      allContacts,
      pairwiseMatches,
    )

    // Step 3: Convert Union-Find groups to contact groups with pairwise matches
    return this.convertToUnifiedContactGroups(
      unionFind,
      allContacts,
      pairwiseMatches,
    )
  }

  /**
   * Build indexes mapping field values to contact indices for unified contacts
   */
  private buildUnifiedMatchingIndexes(contacts: GroupMemberContact[]): {
    relationships: Map<string, number[]>
    phones: Map<string, number[]>
    emails: Map<string, number[]>
  } {
    const relationships = new Map<string, number[]>()
    const phones = new Map<string, number[]>()
    const emails = new Map<string, number[]>()

    contacts.forEach((contact, index) => {
      // Index by relationship external IDs (works for both contact types)
      const relationshipIds = this.getRelationshipExternalIds(contact)
      relationshipIds.forEach(relationshipId => {
        this.addToIndex(relationships, relationshipId, index)
      })

      // Index by phone (works for both contact types)
      const phone = this.getPrimaryPhoneNumber(contact)
      if (phone) {
        this.addToIndex(phones, phone, index)
      }

      // Index by email (works for both contact types)
      const email = this.getPrimaryEmail(contact)
      if (email) {
        this.addToIndex(emails, email.toLowerCase(), index)
      }
    })

    return { relationships, phones, emails }
  }

  /**
   * Get relationship external IDs from either contact type
   */
  private getRelationshipExternalIds(contact: GroupMemberContact): string[] {
    if (this.isUploadContact(contact)) {
      return contact.relationshipExternalId
        ? [contact.relationshipExternalId]
        : []
    } else {
      // Extract all external IDs from existing contact's relationships
      return contact.relationships
        .map(rel => rel.relationshipExternalId)
        .filter((id): id is string => Boolean(id))
    }
  }

  /**
   * Get primary phone number from either contact type (duck typing)
   */
  private getPrimaryPhoneNumber(
    contact: GroupMemberContact,
  ): string | undefined {
    return contact.primaryPhoneNumber
  }

  /**
   * Get primary email from either contact type (duck typing)
   */
  private getPrimaryEmail(contact: GroupMemberContact): string | undefined {
    return contact.primaryEmail
  }

  /**
   * Group contacts with shared values using Union-Find and track pairwise matches
   * Uses prioritized matching - higher priority matches prevent lower priority matching
   */
  private unionContactsBySharedValuesUnified(
    unionFind: UnionFind,
    matchingIndexes: {
      relationships: Map<string, number[]>
      phones: Map<string, number[]>
      emails: Map<string, number[]>
    },
    allContacts: GroupMemberContact[],
    pairwiseMatches: PairwiseMatch[],
  ): void {
    // Track which contact pairs have already been matched at higher priority
    const matchedPairs = new Set<string>()

    const addPairwiseMatch = (
      index1: number,
      index2: number,
      matchType: 'relationship' | 'phone' | 'email',
    ) => {
      const pairKey =
        index1 < index2 ? `${index1}-${index2}` : `${index2}-${index1}`

      // Only add if this pair hasn't been matched at higher priority
      if (!matchedPairs.has(pairKey)) {
        unionFind.union(index1, index2)
        pairwiseMatches.push({
          contact1: allContacts[index1],
          contact2: allContacts[index2],
          matchType,
        })
        matchedPairs.add(pairKey)
      }
    }

    // Priority 1: Relationship external IDs (highest priority)
    matchingIndexes.relationships.forEach(contactIndices => {
      if (contactIndices.length > 1) {
        for (let i = 1; i < contactIndices.length; i++) {
          addPairwiseMatch(contactIndices[0], contactIndices[i], 'relationship')
        }
      }
    })

    // Priority 2: Phone numbers (only for pairs not already matched)
    matchingIndexes.phones.forEach(contactIndices => {
      if (contactIndices.length > 1) {
        for (let i = 1; i < contactIndices.length; i++) {
          addPairwiseMatch(contactIndices[0], contactIndices[i], 'phone')
        }
      }
    })

    // Priority 3: Email addresses (only for pairs not already matched)
    matchingIndexes.emails.forEach(contactIndices => {
      if (contactIndices.length > 1) {
        for (let i = 1; i < contactIndices.length; i++) {
          addPairwiseMatch(contactIndices[0], contactIndices[i], 'email')
        }
      }
    })
  }

  /**
   * Convert Union-Find groups to contact groups with pairwise matches
   */
  private convertToUnifiedContactGroups(
    unionFind: UnionFind,
    allContacts: GroupMemberContact[],
    pairwiseMatches: PairwiseMatch[],
  ): Array<{
    contacts: GroupMemberContact[]
    pairwiseMatches: PairwiseMatch[]
  }> {
    // Group contacts by their Union-Find root
    const groups = new Map<number, GroupMemberContact[]>()

    for (let i = 0; i < allContacts.length; i++) {
      const root = unionFind.find(i)
      if (!groups.has(root)) {
        groups.set(root, [])
      }
      groups.get(root)!.push(allContacts[i])
    }

    // For each group, find its relevant pairwise matches
    return Array.from(groups.values()).map(groupContacts => {
      const groupContactSet = new Set(groupContacts)
      const groupPairwiseMatches = pairwiseMatches.filter(
        match =>
          groupContactSet.has(match.contact1) &&
          groupContactSet.has(match.contact2),
      )

      return {
        contacts: groupContacts,
        pairwiseMatches: groupPairwiseMatches,
      }
    })
  }
}
